import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { spaNavigate } from '../utils/spaNavigate';
import { useMusicPlayer } from '../MusicPlayerContext';
import { shareMusic } from '../utils/shareMusic';
import { downloadMusic, downloadMusicsBatch } from '../utils/downloadMusic';
import { getCachedMusicMetadata, isFileCached, getAllCachedMusics } from '../utils/musicCache';
import { showUiHint } from '../utils/uiHint';
import DownloadDialog from './DownloadDialog';
import { getCoverImageUrl, getLyricsStreamUrl, hasLyrics, hasVideo } from '../utils/musicStreamUrls';
import { isInstrumentalMusic } from '../utils/musicMeta';
import { adminDelete } from '../utils/adminRequest';
import { translateTag } from '../utils/tagTranslations';
import { useUser } from '../UserContext';
import { getFreeMemberNewSongInfo } from '../utils/playbackGate';
import { useContext } from 'react';
import { AdminContext } from '../AdminContext';
import { theme, buttonClass } from '../ui/theme';

const MusicList = ({ 
  languageCategory = null, 
  onMusicSelect = null,
  showAdminActions = false,
  showQuickCategoryBar = null,
  onEdit = null,
  onDelete = null,
  getAuthToken = null,
  onMusicsLoaded = null,
  filterMode = null // 'favorites' 或 'history'，null 表示普通列表
}) => {
  const { i18n, t } = useTranslation();
  const { currentMusic, playing, playlist, setCurrentMusic, setPlaylist, setPlaying, addToPlaylist, setShowPlaylist } = useMusicPlayer();
  const { user } = useUser();
  const { isAdmin: isAdminFromContext } = useContext(AdminContext);

  // 统一成播放器的 iOS 线性图标 + 按钮质感（卡片区域用浅色版本）
  const cx = (...classes) => classes.filter(Boolean).join(' ');
  const IOSIcon = ({ className, children, ...props }) => (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </svg>
  );
  const IconPlay = (p) => (
    <IOSIcon {...p}>
      <path d="M9 7l10 5-10 5V7z" />
    </IOSIcon>
  );
  const IconPause = (p) => (
    <IOSIcon {...p}>
      <path d="M9 7v10" />
      <path d="M15 7v10" />
    </IOSIcon>
  );
  const IconPlus = (p) => (
    <IOSIcon {...p}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </IOSIcon>
  );
  const IconPlaylist = (p) => (
    <IOSIcon {...p}>
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3.5 6h.01" />
      <path d="M3.5 12h.01" />
      <path d="M3.5 18h.01" />
    </IOSIcon>
  );
  const IconInfo = (p) => (
    <IOSIcon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10v6" />
      <path d="M12 7h.01" />
    </IOSIcon>
  );
  const IconHeart = ({ filled = false, ...p }) => (
    <IOSIcon {...p} fill={filled ? 'currentColor' : 'none'}>
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z" />
    </IOSIcon>
  );
  const IconShare = (p) => (
    <IOSIcon {...p}>
      <path d="M12 3v10" />
      <path d="M8 6l4-3 4 3" />
      <path d="M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10" />
    </IOSIcon>
  );
  const IconDownload = (p) => (
    <IOSIcon {...p}>
      <path d="M12 3v10" />
      <path d="M8 10l4 4 4-4" />
      <path d="M5 21h14" />
    </IOSIcon>
  );

  const IconLyrics = (p) => (
    <IOSIcon {...p}>
      <path d="M21 15a4 4 0 01-4 4H9l-4 3v-3H7a4 4 0 01-4-4V7a4 4 0 014-4h10a4 4 0 014 4v8z" />
      <path d="M8 8h8" />
      <path d="M8 12h6" />
    </IOSIcon>
  );
  const cardIconBtnBase = 'h-7 w-7 inline-flex items-center justify-center rounded-md transition-all duration-150 select-none active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/25';
  const cardIconBtnIdle = 'bg-blue-500/10 hover:bg-blue-500/15 border border-blue-400/25 text-white/85 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]';
  const cardIconBtnActive = 'bg-blue-600/90 hover:bg-blue-600 text-white border border-blue-500/40 shadow-[0_10px_20px_rgba(37,99,235,0.22)]';
  const cardIconBtnDisabled = 'opacity-45 cursor-not-allowed';

  // 全站统一按钮（与首页同款）
  const btnOutlineSm = buttonClass({ variant: 'outline', size: 'sm' });
  const btnDangerXs = buttonClass({ variant: 'danger', size: 'xs' });
  const [musics, setMusics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false); // 卡片视图：滚动追加加载
  const [isCategoryChanging, setIsCategoryChanging] = useState(false); // 分类切换中，保持显示当前列表
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // 搜索输入框的值
  const [sortBy, setSortBy] = useState('latest');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [quickCategory, setQuickCategory] = useState(languageCategory);
  const [favoritesStatus, setFavoritesStatus] = useState({}); // {musicId: isFavorite}
  const [cachedStatus, setCachedStatus] = useState({}); // {musicId: isCached}
  const [selectedMusics, setSelectedMusics] = useState(new Set()); // 批量选择的音乐ID
  const [selectMode, setSelectMode] = useState(false); // 是否处于选择模式
  const [viewMode, setViewMode] = useState('card'); // 'card' 或 'list'
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedMusicForDownload, setSelectedMusicForDownload] = useState(null);
  const listActionColSpan = selectMode ? 6 : 5;
  const [centeredCardId, setCenteredCardId] = useState(null); // 当前在屏幕中间的卡片ID
  const cardRefs = useRef({}); // 存储每个卡片的 ref
  const [lyricsPopup, setLyricsPopup] = useState({ show: false, music: null, lyrics: '', position: { x: 0, y: 0 } });
  const lyricsPopupTimeoutRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const pendingPlayMusicRef = useRef(null); // 跟踪刚刚设置的播放音乐（用于处理状态更新延迟问题）
  // 右侧“添加到播放列表（可保存）”菜单
  // 说明：为避免“锚点跑偏/不在点击位置”，这里改成固定弹窗式展示（居中 + 遮罩），不再依赖点击坐标定位
  const [playlistMenu, setPlaylistMenu] = useState({ open: false, music: null });
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [playlistMenuLoading, setPlaylistMenuLoading] = useState(false);
  const playlistMenuRef = useRef(null);
  // 新建播放列表对话框（自定义输入）
  const [createPlaylistDialog, setCreatePlaylistDialog] = useState({ open: false, music: null });
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const sortMenuRef = useRef(null);

  // 免费/未付费会员：新歌限制（UI 层提示与禁用按钮；后端仍会强校验）
  // 注意：使用 AdminContext 中的 isAdmin 状态，而不仅仅是 showAdminActions prop
  // 这样即使在没有传递 showAdminActions 的情况下（如首页），管理员也能正常播放新歌
  const getGateInfo = useCallback((music) => {
    return getFreeMemberNewSongInfo({
      music,
      user,
      isAdmin: !!showAdminActions || !!isAdminFromContext
    });
  }, [user, showAdminActions, isAdminFromContext]);

  const getGateHintText = useCallback((gate) => {
    const days = Number(gate?.daysLeft || 0);
    return t('musicList.freeMemberNewSongHint', { days });
  }, [t]);

  const handleGateBlockedClick = useCallback((e, gate) => {
    try {
      if (e) e.stopPropagation();
      const anchorEl = e?.currentTarget || null;
      showUiHint(getGateHintText(gate), { anchorEl, duration: 2200 });
    } catch (_) {}
  }, [getGateHintText]);

  // Debug 开关：默认关闭，避免控制台刷屏
  // 在浏览器控制台执行：localStorage.setItem('debug_musiclist','1') 后刷新即可开启
  const isDebugMusicList =
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    (() => {
      try {
        return window.localStorage?.getItem('debug_musiclist') === '1' || window.__DEBUG_MUSICLIST__ === true;
      } catch (_) {
        return false;
      }
    })();

  // 某些父组件会每次 render 传入新函数，导致 useCallback 变化 -> useEffect 反复触发 fetch
  // 用 ref 保持“最新函数”，同时让 fetchMusics 依赖保持稳定
  const onMusicsLoadedRef = useRef(onMusicsLoaded);
  useEffect(() => {
    onMusicsLoadedRef.current = onMusicsLoaded;
  }, [onMusicsLoaded]);

  const getAuthTokenRef = useRef(getAuthToken);
  useEffect(() => {
    getAuthTokenRef.current = getAuthToken;
  }, [getAuthToken]);

  useEffect(() => {
    if (!sortMenuOpen) return;
    const handlePointerDown = (e) => {
      if (!sortMenuRef.current) return;
      if (!sortMenuRef.current.contains(e.target)) {
        setSortMenuOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSortMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sortMenuOpen]);

  // 根据视图模式设置每页数量
  const pageSize = viewMode === 'list' ? 100 : 20;

  // 格式化数字：上万显示"万"，上亿显示"亿"
  const formatCount = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 100000000) {
      return Math.floor(num / 100000000) + '亿';
    }
    if (num >= 10000) {
      return Math.floor(num / 10000) + '万';
    }
    return num.toString();
  };

  // 外部分类变化时同步到快捷分类（例如顶部导航切换中文/日文/英文...）
  useEffect(() => {
    setQuickCategory(languageCategory);
  }, [languageCategory]);

  // 执行搜索
  const handleSearch = () => {
    if (searchQuery) {
      // 如果正在搜索，点击停止搜索
      handleStopSearch();
    } else {
      // 执行搜索
      if (searchInput.trim()) {
        setSearchQuery(searchInput.trim());
        setPage(1); // 重置到第一页
      }
    }
  };

  // 停止搜索
  const handleStopSearch = () => {
    setSearchQuery('');
    setSearchInput('');
    setPage(1); // 重置到第一页
  };

  // 处理回车键搜索
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchQuery) {
        // 如果正在搜索，按回车停止搜索
        handleStopSearch();
      } else {
        // 执行搜索
        handleSearch();
      }
    }
  };

  // 获取用户ID
  const getUserId = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'user_id') return value;
    }
    const userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    document.cookie = `user_id=${userId}; path=/; max-age=31536000`;
    return userId;
  };

  // 打开“播放列表菜单”：展示已有播放列表 + 顶部“+新建播放列表”
  const openPlaylistMenu = async (music, e) => {
    try {
      e?.stopPropagation();
      e?.preventDefault?.();
    } catch (_) {}

    // 弹窗式菜单：只记录当前 music
    setPlaylistMenu({ open: true, music });
    setPlaylistMenuLoading(true);

    try {
      const userId = getUserId();
      const resp = await axios.get('/api/music/playlist', {
        headers: {
          'X-User-ID': userId,
          'Cache-Control': 'no-cache'
        },
        params: { _t: Date.now() }
      });
      if (resp?.data?.success) {
        setUserPlaylists(Array.isArray(resp.data.data?.playlists) ? resp.data.data.playlists : []);
      } else {
        setUserPlaylists([]);
      }
    } catch (err) {
      console.error('Fetch playlists error:', err);
      setUserPlaylists([]);
    } finally {
      setPlaylistMenuLoading(false);
    }
  };

  const closePlaylistMenu = useCallback(() => {
    setPlaylistMenu({ open: false, music: null });
    setPlaylistMenuLoading(false);
  }, []);

  // 按 ESC 关闭菜单
  useEffect(() => {
    if (!playlistMenu.open) return;
    const onKey = (ev) => {
      if (ev?.key === 'Escape') closePlaylistMenu();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
    };
  }, [playlistMenu.open, closePlaylistMenu]);

  const addMusicToSavedPlaylist = async (playlistObj, music, anchorEl) => {
    try {
      const pid = playlistObj?.id;
      const mid = music?.id;
      if (!pid || !mid) return;

      const exists = Array.isArray(playlistObj.music_ids) ? playlistObj.music_ids.includes(mid) : false;
      if (exists) {
        showUiHint('已在该播放列表中', { anchorEl, duration: 1200 });
        closePlaylistMenu();
        return;
      }

      const nextIds = [...(Array.isArray(playlistObj.music_ids) ? playlistObj.music_ids : []), mid];
      const userId = getUserId();
      const resp = await axios.put(`/api/music/playlist/${pid}`, { music_ids: nextIds }, {
        headers: {
          'X-User-ID': userId,
          'Content-Type': 'application/json'
        }
      });
      if (resp?.data?.success) {
        showUiHint(`已添加到播放列表：${playlistObj?.name || ''}`, { anchorEl, duration: 1400 });
      } else {
        showUiHint('添加失败', { anchorEl, duration: 1400 });
      }
    } catch (err) {
      console.error('Add to playlist error:', err);
      showUiHint('添加失败', { anchorEl, duration: 1400 });
    } finally {
      closePlaylistMenu();
    }
  };

  const createPlaylistAndAdd = async (music, name, anchorEl) => {
    try {
      if (!name || !String(name).trim()) return;
      const userId = getUserId();
      const resp = await axios.post('/api/music/playlist', {
        name: String(name).trim(),
        description: '',
        is_public: false,
        music_ids: music?.id ? [music.id] : []
      }, {
        headers: {
          'X-User-ID': userId,
          'Content-Type': 'application/json'
        }
      });
      if (resp?.data?.success) {
        showUiHint('已创建并加入播放列表', { anchorEl, duration: 1400 });
      } else {
        showUiHint(resp?.data?.error || resp?.data?.message || '创建失败', { anchorEl, duration: 1600 });
      }
    } catch (err) {
      console.error('Create playlist error:', err);
      showUiHint('创建失败', { anchorEl, duration: 1600 });
    } finally {
      // 创建对话框关闭；播放列表选择弹窗也关闭
      setCreatePlaylistDialog({ open: false, music: null });
      setNewPlaylistName('');
      closePlaylistMenu();
    }
  };

  const openCreatePlaylistDialog = (music) => {
    setCreatePlaylistDialog({ open: true, music });
    setNewPlaylistName('');
  };

  const closeCreatePlaylistDialog = () => {
    setCreatePlaylistDialog({ open: false, music: null });
    setNewPlaylistName('');
  };

  // 检查收藏状态（使用批处理避免同时发起过多请求，带重试机制）
  const checkFavoritesStatus = useCallback(async (musics, opts = {}) => {
    try {
      const userId = getUserId();
      const status = {};
      const replace = !!opts.replace;
      
      // 批处理大小：每次处理 5 个请求，避免服务器过载
      const batchSize = 5;
      const delayBetweenBatches = 100; // 批次之间的延迟（毫秒）
      const maxRetries = 2; // 最大重试次数
      
      // 带重试的请求函数
      const fetchWithRetry = async (music, retries = 0) => {
        try {
          const response = await axios.get(`/api/music/${music.id}/favorite`, {
            headers: { 'X-User-ID': userId },
            timeout: 5000 // 5秒超时
          });
          if (response.data.success) {
            return response.data.data.isFavorite;
          }
          return false;
        } catch (e) {
          // 如果是 503 错误且还有重试次数，则重试
          const isRetryable = e.response?.status === 503 || e.code === 'ECONNABORTED';
          if (isRetryable && retries < maxRetries) {
            // 指数退避：第一次重试等待 200ms，第二次等待 400ms
            await new Promise(resolve => setTimeout(resolve, 200 * Math.pow(2, retries)));
            return fetchWithRetry(music, retries + 1);
          }
          // 静默失败，默认设为 false
          return false;
        }
      };
      
      for (let i = 0; i < musics.length; i += batchSize) {
        const batch = musics.slice(i, i + batchSize);
        
        await Promise.all(
          batch.map(async (music) => {
            status[music.id] = await fetchWithRetry(music);
          })
        );
        
        // 如果不是最后一批，等待一段时间再处理下一批
        if (i + batchSize < musics.length) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
        }
      }
      
      setFavoritesStatus(prev => (replace ? status : ({ ...prev, ...status })));
    } catch (error) {
      console.error('Check favorites error:', error);
    }
  }, []);

  // 检查缓存状态（同时检查元数据和音频文件）
  const checkCachedStatus = useCallback(async (musics, opts = {}) => {
    try {
      const status = {};
      const replace = !!opts.replace;
      
      await Promise.all(
        musics.map(async (music) => {
          try {
            // 检查 IndexedDB 中是否有元数据
            const cachedMetadata = await getCachedMusicMetadata(music.id);
            if (!cachedMetadata) {
              status[music.id] = false;
              return;
            }
            
            // 检查音频文件是否已缓存
            const audioPath = music.file_path;
            if (audioPath) {
              const isAudioCached = await isFileCached(audioPath);
              status[music.id] = isAudioCached;
            } else {
              status[music.id] = false;
            }
          } catch (e) {
            status[music.id] = false;
          }
        })
      );
      
      setCachedStatus(prev => (replace ? status : ({ ...prev, ...status })));
    } catch (error) {
      console.error('Check cached status error:', error);
    }
  }, []);

  /**
   * 离线模式下的数据获取逻辑（封装为独立函数供复用）
   */
  const handleOfflineDataFetch = useCallback(async (params, isAppend) => {
    try {
      const allCached = await getAllCachedMusics();
      if (!allCached || allCached.length === 0) return false;

      const effectiveCategory = params.lang;
      const query = params.search ? params.search.toLowerCase() : '';
      
      // 1. 过滤：分类和搜索
      let filtered = allCached.filter(m => {
        // 分类过滤
        if (effectiveCategory && m.language !== effectiveCategory) return false;
        // 搜索过滤
        if (query) {
          const title = (m.title || '').toLowerCase();
          const title_zh = (m.title_zh_cn || '').toLowerCase();
          const title_ja = (m.title_ja || '').toLowerCase();
          const desc = (m.description || '').toLowerCase();
          const tags = Array.isArray(m.tags) ? m.tags.join(' ').toLowerCase() : '';
          
          if (!title.includes(query) && 
              !title_zh.includes(query) && 
              !title_ja.includes(query) && 
              !desc.includes(query) && 
              !tags.includes(query)) {
            return false;
          }
        }
        return true;
      });
      
      // 2. 排序（简单模拟最新优先）
      filtered.sort((a, b) => new Date(b.cached_at || 0) - new Date(a.cached_at || 0));
      
      // 3. 分页处理
      const totalCount = filtered.length;
      const totalPgs = Math.ceil(totalCount / pageSize);
      
      const batch = filtered.slice((page - 1) * pageSize, page * pageSize);
      
      if (batch.length > 0 || page === 1) {
        let mergedMusics = batch;
        setMusics(prev => {
          mergedMusics = isAppend ? [...(Array.isArray(prev) ? prev : []), ...batch] : batch;
          return mergedMusics;
        });
        setTotal(totalCount);
        setTotalPages(totalPgs);
        
        if (typeof onMusicsLoadedRef.current === 'function') {
          onMusicsLoadedRef.current(mergedMusics);
        }
        
        const cachedStatusObj = {};
        batch.forEach(m => { cachedStatusObj[m.id] = true; });
        setCachedStatus(prev => (isAppend ? ({ ...prev, ...cachedStatusObj }) : cachedStatusObj));
        
        setError(null);
        return true;
      }
      return false;
    } catch (e) {
      console.error('[MusicList] handleOfflineDataFetch error:', e);
      return false;
    }
  }, [page, pageSize, setMusics, setTotal, setTotalPages, setCachedStatus, setError]);

  const fetchMusics = useCallback(async () => {
    const isCardInfinite = viewMode === 'card';
    const isAppend = isCardInfinite && page > 1;
    if (isAppend) {
      setLoadingMore(true);
    } else {
      // 如果不是追加模式，且当前有数据，说明是分类切换，使用过渡状态
      if (musics.length > 0) {
        setIsCategoryChanging(true);
      } else {
        setLoading(true);
      }
    }
    setError(null);
    
    try {
      const params = {
        page,
        pageSize,
        sort: sortBy
      };

      const effectiveCategory = (quickCategory === undefined ? languageCategory : quickCategory);
      if (effectiveCategory) {
        params.lang = effectiveCategory;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const isAdminMode = !!showAdminActions;
      const token = (isAdminMode && getAuthTokenRef.current) ? getAuthTokenRef.current() : null;

      // --- 离线模式：直接拦截并使用本地数据 ---
      // 避免 Service Worker 缓存的 API 快照不完整（如只有 1-3 条）导致的问题
      if (!navigator.onLine && !isAdminMode && !filterMode) {
        console.info('[MusicList] System is offline, using IndexedDB data source.');
        const handled = await handleOfflineDataFetch(params, isAppend);
        if (handled) return;
      }
      
      // 根据 filterMode 选择 API 端点
      let url;
      let headers = {};
      
      if (filterMode === 'favorites') {
        url = '/api/music/favorites';
        const userId = getUserId();
        headers['X-User-ID'] = userId;
      } else if (filterMode === 'history') {
        url = '/api/music/history';
        const userId = getUserId();
        headers['X-User-ID'] = userId;
      } else {
        url = isAdminMode ? '/api/music/admin/list' : '/api/music/list';
        if (isAdminMode) {
          headers = {
            'Cache-Control': 'no-store',
            'Pragma': 'no-cache',
            'X-Tenryu-No-Cache': '1'
          };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        }
      }
      
      if (isDebugMusicList) {
        console.debug('[MusicList] Fetching music list:', {
          filterMode,
          isAdminMode,
          url,
          hasToken: !!token,
          params,
          effectiveCategory,
          languageCategory,
          quickCategory
        });
      }
      
      // 带重试的请求函数
      const fetchWithRetry = async (retries = 0) => {
        const maxRetries = 2;
        try {
          return isAdminMode && getAuthTokenRef.current
            ? await axios.get(url, {
                params,
                headers: Object.keys(headers).length > 0 ? headers : undefined,
                withCredentials: true,
                timeout: 10000 // 10秒超时
              })
            : await axios.get(url, {
                params,
                headers: Object.keys(headers).length > 0 ? headers : undefined,
                timeout: 10000 // 10秒超时
              });
        } catch (e) {
          // 如果是 503 错误或超时，且还有重试次数，则重试
          const isRetryable = e.response?.status === 503 || e.code === 'ECONNABORTED' || !e.response;
          if (isRetryable && retries < maxRetries) {
            // 指数退避：第一次重试等待 500ms，第二次等待 1000ms
            await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, retries)));
            return fetchWithRetry(retries + 1);
          }
          throw e;
        }
      };
      
      // 管理员模式使用统一的认证请求，普通模式使用普通请求
      const response = await fetchWithRetry();
      
      if (isDebugMusicList) {
        console.debug('[MusicList] Response:', {
          success: response.data.success,
          musicsCount: response.data.data?.musics?.length || 0,
          total: response.data.data?.total || 0,
          page: response.data.data?.page,
          pageSize: response.data.data?.pageSize,
          totalPages: response.data.data?.totalPages
        });
      }
      
      if (response.data.success) {
        // 兼容历史 API 的旧格式（返回 history 数组）
        const batch = response.data.data.musics || response.data.data.history || [];
        const total = response.data.data.total || batch.length;
        const totalPages = response.data.data.totalPages || Math.ceil(total / pageSize);

        let mergedMusics = batch;
        setMusics(prev => {
          mergedMusics = isAppend ? [...(Array.isArray(prev) ? prev : []), ...batch] : batch;
          return mergedMusics;
        });
        setTotal(total);
        setTotalPages(totalPages);
        
        // 通知父组件音乐列表已加载
        if (typeof onMusicsLoadedRef.current === 'function') {
          onMusicsLoadedRef.current(mergedMusics);
        }
        
        // 检查收藏状态（收藏模式不需要检查，因为都是已收藏的）
        if (filterMode !== 'favorites') {
          checkFavoritesStatus(batch, { replace: !isAppend });
        } else {
          // 收藏模式：所有音乐都是已收藏的
          const favoritesStatusObj = {};
          batch.forEach(music => {
            favoritesStatusObj[music.id] = true;
          });
          setFavoritesStatus(prev => (isAppend ? ({ ...prev, ...favoritesStatusObj }) : favoritesStatusObj));
        }
        // 检查缓存状态
        checkCachedStatus(batch, { replace: !isAppend });
      } else {
        setError(response.data.error || t('musicList.fetchFailed'));
      }
    } catch (err) {
      console.error('Fetch musics error:', err);
      
      // --- 离线模式彻底解决方案 ---
      // 如果请求失败（网络错误或 503 离线提示），且不是管理员模式、收藏模式、历史模式，则尝试从 IndexedDB 加载
      const isNetworkError = !err.response || err.response.status === 503 || !navigator.onLine;
      const isNormalList = !filterMode && !showAdminActions;
      
      if (isNetworkError && isNormalList) {
        console.warn('[MusicList] Request failed or offline, falling back to IndexedDB...');
        const params = {
          lang: (quickCategory === undefined ? languageCategory : quickCategory),
          search: searchQuery
        };
        const handled = await handleOfflineDataFetch(params, isAppend);
        if (handled) return;
      }
      
      setError(err.response?.data?.error || t('musicList.fetchFailed'));
    } finally {
      if (isAppend) {
        setLoadingMore(false);
      } else {
        setLoading(false);
        setIsCategoryChanging(false); // 分类切换完成
      }
    }
  }, [page, pageSize, sortBy, quickCategory, languageCategory, searchQuery, showAdminActions, checkFavoritesStatus, checkCachedStatus, isDebugMusicList, filterMode, viewMode, handleOfflineDataFetch, t, musics.length]);

  useEffect(() => {
    fetchMusics();
  }, [fetchMusics]);

  // 卡片视图：滚动到列表底部时自动加载更多（仍是"单页"，只是按需追加）
  useEffect(() => {
    if (viewMode !== 'card') return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    // 没有更多页时无需监听
    if (!(page < totalPages)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries && entries[0];
        if (!entry || !entry.isIntersecting) return;
        if (loading || loadingMore) return;
        if (page < totalPages) setPage(p => p + 1);
      },
      { root: null, rootMargin: '800px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => {
      try { observer.disconnect(); } catch (_) {}
    };
  }, [viewMode, page, totalPages, loading, loadingMore]);

  // 监听缓存清理事件，刷新缓存状态
  useEffect(() => {
    const handleCacheCleared = () => {
      // 清空所有缓存状态标志
      setCachedStatus({});
      // 如果当前有音乐列表，重新检查缓存状态
      if (musics && musics.length > 0) {
        checkCachedStatus(musics, { replace: true });
      }
    };

    window.addEventListener('tenryu:cache-cleared', handleCacheCleared);
    return () => {
      window.removeEventListener('tenryu:cache-cleared', handleCacheCleared);
    };
  }, [musics, checkCachedStatus]);

  // 检测卡片是否在屏幕中间，自动显示播放按钮
  useEffect(() => {
    if (viewMode !== 'card') return;

    const observer = new IntersectionObserver(
      (entries) => {
        // 找到最接近屏幕中心的可见卡片
        let closestEntry = null;
        let closestDistance = Infinity;
        const viewportCenter = window.innerHeight / 2;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const rect = entry.boundingClientRect;
            const cardCenter = rect.top + rect.height / 2;
            const distance = Math.abs(cardCenter - viewportCenter);
            
            if (distance < closestDistance) {
              closestDistance = distance;
              closestEntry = entry;
            }
          }
        });

        if (closestEntry) {
          const cardId = parseInt(closestEntry.target.dataset.musicId);
          if (cardId && !isNaN(cardId)) {
            setCenteredCardId(cardId);
          }
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -20% 0px', // 只检测屏幕中间 60% 的区域
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
      }
    );

    // 观察所有卡片
    Object.values(cardRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, [viewMode, musics]);

  // 监听音乐缓存完成事件，更新对应音乐的缓存状态
  useEffect(() => {
    const handleMusicCached = async (event) => {
      const { musicId } = event.detail || {};
      if (!musicId) return;
      
      // 检查当前音乐列表中是否有这首音乐
      const music = musics.find(m => m.id === musicId);
      if (music) {
        // 重新检查这首音乐的缓存状态
        await checkCachedStatus([music], { replace: false });
      }
    };

    window.addEventListener('tenryu:music-cached', handleMusicCached);
    return () => {
      window.removeEventListener('tenryu:music-cached', handleMusicCached);
    };
  }, [musics, checkCachedStatus]);

  const handleDelete = async (musicId) => {
    if (!window.confirm(t('musicList.confirmDelete'))) {
      return;
    }

    try {
      if (!showAdminActions) {
        alert(t('musicList.adminRequired'));
        return;
      }

      const response = await adminDelete(`/api/music/${musicId}`, {
        isAdmin: showAdminActions,
        getAuthToken
      });

      if (response.data.success) {
        // 先本地移除，避免后续列表刷新/缓存回退导致“看起来没删掉”
        setMusics(prev => (Array.isArray(prev) ? prev.filter(m => m && m.id !== musicId) : prev));
        setTotal(prev => (Number.isFinite(prev) ? Math.max(0, prev - 1) : prev));
        // 再刷新列表（校准分页/总数）
        fetchMusics();
        if (onDelete) {
          onDelete(musicId);
        }
      } else {
        alert(response.data.error || t('musicList.deleteFailed'));
      }
    } catch (err) {
      console.error('Delete music error:', err);
      // 如果是 404 错误，可能是记录已经不存在，尝试刷新列表
      if (err.response?.status === 404) {
        console.warn('Music not found (may have been deleted already), refreshing list...');
        // 先本地移除，然后刷新列表
        setMusics(prev => (Array.isArray(prev) ? prev.filter(m => m && m.id !== musicId) : prev));
        setTotal(prev => (Number.isFinite(prev) ? Math.max(0, prev - 1) : prev));
        fetchMusics();
        // 不显示错误，因为记录可能已经不存在了
        return;
      }
      alert(err.response?.data?.error || err.message || t('musicList.deleteFailed'));
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatReleaseDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (!Number.isFinite(date.getTime())) return String(dateString);
    const lang = String(i18n.language || 'cn');
    const localeMap = {
      cn: 'zh-CN',
      tw: 'zh-TW',
      ja: 'ja-JP',
      en: 'en-US'
    };
    const locale = localeMap[lang] || 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 切换收藏
  const toggleFavorite = async (musicId, e) => {
    e.stopPropagation();

    // 保存按钮元素引用，避免异步操作后 e.currentTarget 失效
    const buttonEl = e?.currentTarget || null;

    try {
      const userId = getUserId();
      const isFavorite = favoritesStatus[musicId];

      const response = isFavorite
        ? await axios.delete(`/api/music/${musicId}/favorite`, {
            headers: { 'X-User-ID': userId }
          })
        : await axios.post(`/api/music/${musicId}/favorite`, {}, {
            headers: { 'X-User-ID': userId }
          });

      if (response.data.success) {
        // 更新收藏状态
        setFavoritesStatus(prev => ({
          ...prev,
          [musicId]: !isFavorite
        }));

        // 显示提示
        const hintText = !isFavorite ? t('musicList.favorited') : t('musicList.unfavorited');
        if (buttonEl) {
          showUiHint(hintText, { anchorEl: buttonEl, duration: 1200 });
        } else {
          // 如果按钮元素不可用，显示底部 toast
          showUiHint(hintText, { duration: 1200 });
        }

        // 如果是收藏模式且取消收藏，从列表中移除
        if (filterMode === 'favorites' && isFavorite) {
          setMusics(prevMusics => prevMusics.filter(music => music.id !== musicId));
          setTotal(prev => Math.max(0, prev - 1));
        } else {
          // 更新收藏计数
          setMusics(prevMusics =>
            prevMusics.map(music =>
              music.id === musicId
                ? {
                    ...music,
                    favoritesCount: isFavorite
                      ? Math.max(0, (music.favoritesCount || 0) - 1)
                      : (music.favoritesCount || 0) + 1
                  }
                : music
            )
          );
        }
      }
    } catch (error) {
      console.error('Toggle favorite error:', error);
      // 显示错误提示
      const buttonEl = e?.currentTarget || null;
      if (buttonEl) {
        showUiHint(t('musicList.operationFailed'), { anchorEl: buttonEl, duration: 2000 });
        } else {
          showUiHint(t('musicList.operationFailed'), { duration: 2000 });
      }
    }
  };

  // 下载音乐（打开下载对话框）
  const handleDownload = (music, e) => {
    if (e) e.stopPropagation();
    
    if (!music.allow_download) {
      alert(t('musicList.downloadNotAllowed'));
      return;
    }
    
    setSelectedMusicForDownload(music);
    setDownloadDialogOpen(true);
  };

  // 确认下载（从对话框）
  const handleDownloadConfirm = async (formats, includeLyrics) => {
    if (!selectedMusicForDownload) return;
    
    // 使用统一的下载函数，下载成功后更新下载计数
    await downloadMusic(
      selectedMusicForDownload.id,
      selectedMusicForDownload.allow_download,
      () => {
        // 下载成功回调：增加下载计数
        setMusics(prevMusics => 
          prevMusics.map(m => 
            m.id === selectedMusicForDownload.id 
              ? { ...m, download_count: (m.download_count || 0) + 1 }
              : m
          )
        );
      },
      formats,
      includeLyrics
    );
  };

  // 分享音乐
  const handleShare = async (music, e) => {
    e.stopPropagation();
    
    const currentLang = i18n.language || 'cn';
    
    // 使用统一的分享函数，分享成功后更新分享计数
    await shareMusic(music.id, currentLang, () => {
      // 分享成功回调：增加分享计数
      setMusics(prevMusics => 
        prevMusics.map(m => 
          m.id === music.id 
            ? { ...m, share_count: (m.share_count || 0) + 1 }
            : m
        )
      );
    });
  };

  // 切换选择模式
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    if (selectMode) {
      setSelectedMusics(new Set());
    }
  };

  // 切换单个音乐的选择状态
  const toggleMusicSelection = (musicId) => {
    setSelectedMusics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(musicId)) {
        newSet.delete(musicId);
      } else {
        newSet.add(musicId);
      }
      return newSet;
    });
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedMusics.size === musics.length) {
      setSelectedMusics(new Set());
    } else {
      setSelectedMusics(new Set(musics.map(m => m.id)));
    }
  };

  // 批量添加到播放列表
  const handleBatchAddToPlaylist = () => {
    if (selectedMusics.size === 0) {
      alert(t('musicList.pleaseSelectMusic'));
      return;
    }
    
    const selectedMusicList = musics.filter(m => selectedMusics.has(m.id));
    selectedMusicList.forEach(music => {
      if (onMusicSelect) {
        onMusicSelect(music);
      }
    });
    
    alert(t('musicList.addedToPlaylist', { count: selectedMusics.size }));
    setSelectedMusics(new Set());
    setSelectMode(false);
  };

  // 批量添加到收藏
  const handleBatchAddToFavorites = async () => {
    if (selectedMusics.size === 0) {
      alert(t('musicList.pleaseSelectMusic'));
      return;
    }

    try {
      const userId = getUserId();
      let successCount = 0;
      
      await Promise.all(
        Array.from(selectedMusics).map(async (musicId) => {
          try {
            const response = await axios.post(`/api/music/${musicId}/favorite`, {}, {
              headers: { 'X-User-ID': userId }
            });
            if (response.data.success) {
              successCount++;
            }
          } catch (e) {
            // 忽略已收藏的错误
          }
        })
      );
      
      alert(t('musicList.favoritedCount', { count: successCount }));
      setSelectedMusics(new Set());
      setSelectMode(false);
      
      // 刷新收藏状态
      checkFavoritesStatus(musics);
    } catch (error) {
      console.error('Batch add to favorites error:', error);
      alert(t('musicList.operationFailed'));
    }
  };

  // 批量下载
  const handleBatchDownload = () => {
    if (selectedMusics.size === 0) {
      alert(t('musicList.pleaseSelectMusic'));
      return;
    }

    const selectedMusicList = musics.filter(m => selectedMusics.has(m.id));
    
    // 使用统一的批量下载函数
    downloadMusicsBatch(selectedMusicList, () => {
      // 批量下载完成，清除选择
      setSelectedMusics(new Set());
      setSelectMode(false);
    });
  };

  // 批量删除（仅管理员）
  const handleBatchDelete = async () => {
    if (selectedMusics.size === 0) {
      alert(t('musicList.pleaseSelectMusic'));
      return;
    }

    if (!window.confirm(t('musicList.confirmBatchDelete', { count: selectedMusics.size }))) {
      return;
    }

    if (!showAdminActions) {
      alert(t('musicList.adminRequired'));
      return;
    }

    try {
      let successCount = 0;
      
      await Promise.all(
        Array.from(selectedMusics).map(async (musicId) => {
          try {
            const response = await adminDelete(`/api/music/${musicId}`, {
              isAdmin: showAdminActions,
              getAuthToken
            });
            if (response.data.success) {
              successCount++;
            }
          } catch (e) {
            console.error(`Delete music ${musicId} error:`, e);
          }
        })
      );
      
      alert(t('musicList.deletedCount', { count: successCount }));
      setSelectedMusics(new Set());
      setSelectMode(false);
      
      // 刷新列表
      fetchMusics();
    } catch (error) {
      console.error('Batch delete error:', error);
      alert(t('musicList.deleteFailedRetry'));
    }
  };

  const getCurrentLanguageTitle = (music) => {
    const lang = i18n.language || 'cn';
    switch (lang) {
      case 'cn':
        return music.title_zh_cn || music.title;
      case 'tw':
        return music.title_zh_tw || music.title;
      case 'ja':
        return music.title_ja || music.title;
      case 'en':
        return music.title_en || music.title;
      default:
        return music.title;
    }
  };

  // 获取当前语言描述（副标题）
  const getCurrentLanguageDescription = (music) => {
    const lang = i18n.language || 'cn';
    switch (lang) {
      case 'cn':
        return music.description_zh_cn || music.description;
      case 'tw':
        return music.description_zh_tw || music.description;
      case 'ja':
        return music.description_ja || music.description;
      case 'en':
        return music.description_en || music.description;
      default:
        return music.description;
    }
  };

  // 加载歌词txt文件内容
  const loadLyricsText = async (music) => {
    if (!music || !music.lyrics_type) {
      console.log('No lyrics type for music:', music?.id);
      return null;
    }
    
    try {
      // 统一：优先使用同一套“歌词 URL 生成逻辑”（支持开关：可走 stream 或直链）
      const streamUrl = getLyricsStreamUrl(music);
      let lyricsUrl = streamUrl;
      if (!lyricsUrl) return null;

      // 如果不是走 stream，且歌词是 lrc/srt，则尝试加载同名 .txt
      const isStream = typeof lyricsUrl === 'string' && lyricsUrl.includes('/api/music/') && lyricsUrl.includes('/stream');
      if (!isStream) {
        if (lyricsUrl.endsWith('.lrc')) {
          lyricsUrl = lyricsUrl.replace(/\.lrc$/, '.txt');
        } else if (lyricsUrl.endsWith('.srt')) {
          lyricsUrl = lyricsUrl.replace(/\.srt$/, '.txt');
        }
      }
      
      // 隐藏歌词文件路径，只显示文件名
      const lyricsUrlFileName = lyricsUrl ? lyricsUrl.split('/').pop() || lyricsUrl : 'N/A';
      console.log('Trying to load lyrics from:', lyricsUrlFileName);
      const response = await fetch(lyricsUrl);
      if (response.ok) {
        const text = await response.text();
        // 关键防呆：如果拿到的是 SPA 的 index.html（text/html），不要当歌词展示
        const ct = (response.headers.get('content-type') || '').toLowerCase();
        const looksLikeHtml =
          ct.includes('text/html') ||
          String(text).trim().toLowerCase().startsWith('<!doctype html') ||
          String(text).includes('<div id="root"></div>');
        if (looksLikeHtml) {
          console.warn('Lyrics response looks like HTML (likely SPA fallback). Treat as missing.', { lyricsUrl, contentType: ct });
          return null;
        }
        console.log('Lyrics loaded successfully, length:', text.length);
        // 统一：尽量返回“纯文本”（去掉可能混入的时间标签）
        const plain = String(text)
          .replace(/\[\d{1,2}:\d{2}(?:\.\d{2})?\]/g, '') // LRC
          .replace(/\d{1,2}:\d{2}:\d{2},\d{3}\s*-->\s*\d{1,2}:\d{2}:\d{2},\d{3}/g, '') // SRT
          .trim();
        return plain;
      } else {
        console.log('Failed to load lyrics from:', lyricsUrlFileName, 'Status:', response.status);
      }
      
      // 如果.txt不存在，尝试直接加载原始文件（可能是txt格式）
      console.log('No lyrics found for music:', music.id);
      return null;
    } catch (error) {
      console.error('Load lyrics error:', error);
      return null;
    }
  };

  // 显示歌词弹窗
  const showLyricsPopup = async (music, event) => {
    if (!music || !music.lyrics_type) {
      console.log('Cannot show lyrics popup: no music or lyrics_type');
      return;
    }
    
    // 如果已经显示同一首音乐的歌词，直接返回
    if (lyricsPopup.show && lyricsPopup.music && lyricsPopup.music.id === music.id) {
      console.log('Lyrics popup already showing for this music');
      return;
    }
    
    // 使用 requestAnimationFrame 延迟 DOM 读取，避免 forced reflow
    let rect = null;
    const element = event?.currentTarget || event?.target;
    if (element) {
      // 在下一帧读取位置信息，避免强制重排
      await new Promise(resolve => requestAnimationFrame(resolve));
      rect = element.getBoundingClientRect();
    }
    
    // 如果没有获取到位置信息，使用默认位置
    if (!rect) {
      console.warn('Could not get element position, using default');
      rect = {
        right: window.innerWidth / 2,
        top: window.innerHeight / 2,
        left: window.innerWidth / 2 - 200
      };
    }
    
    console.log('Loading lyrics for music:', music.id, music.lyrics_type);
    const lyricsText = await loadLyricsText(music);
    
    if (lyricsText && lyricsText.trim()) {
      const popupWidth = 400; // 弹窗宽度
      const popupHeight = 200; // 弹窗高度（3/4）
      
      // 计算位置，避免超出屏幕
      let x = rect.right + 10;
      let y = rect.top - popupHeight/2;
      
      // 如果右侧空间不足，显示在左侧
      if (x + popupWidth > window.innerWidth) {
        x = rect.left - popupWidth - 10;
      }
      
      // 如果下方空间不足，向上调整
      if (y + popupHeight > window.innerHeight) {
        y = window.innerHeight - popupHeight - 10;
      }
      
      // 确保不超出左边界和上边界
      x = Math.max(10, x);
      y = Math.max(10, y);
      
      console.log('Setting lyrics popup at position:', x, y);
      setLyricsPopup({
        show: true,
        music: music,
        lyrics: lyricsText,
        position: {
          x: x,
          y: y
        }
      });
    } else {
      console.log('No lyrics text loaded, cannot show popup');
    }
  };

  // 隐藏歌词弹窗
  const hideLyricsPopup = () => {
    // 清除之前的定时器
    if (lyricsPopupTimeoutRef.current) {
      clearTimeout(lyricsPopupTimeoutRef.current);
    }
    // 延迟隐藏，给鼠标移动到弹窗的时间
    lyricsPopupTimeoutRef.current = setTimeout(() => {
      setLyricsPopup(prev => ({ ...prev, show: false }));
    }, 200);
  };

  // 保持弹窗显示
  const keepLyricsPopup = () => {
    // 清除隐藏定时器
    if (lyricsPopupTimeoutRef.current) {
      clearTimeout(lyricsPopupTimeoutRef.current);
      lyricsPopupTimeoutRef.current = null;
    }
  };

  // 处理播放按钮点击
  const handlePlayButtonClick = (music, e) => {
    e.stopPropagation(); // 阻止事件冒泡到卡片点击
    
    // 如果当前正在播放这首音乐，则暂停
    // 注意：需要检查 currentMusic 是否存在，因为状态更新是异步的
    // 同时检查 pendingPlayMusicRef，处理刚刚通过"添加到播放序列"设置的播放状态
    const isCurrentlyPlaying = (currentMusic && currentMusic.id === music.id && playing) ||
                               (pendingPlayMusicRef.current && pendingPlayMusicRef.current.id === music.id);
    
    if (isCurrentlyPlaying) {
      setPlaying(false);
      // 清除待播放标记
      if (pendingPlayMusicRef.current && pendingPlayMusicRef.current.id === music.id) {
        pendingPlayMusicRef.current = null;
      }
      return;
    }
    
    // 如果当前没有播放任何音乐，或者播放的是其他音乐，则播放这首音乐
    // 设置当前音乐并开始播放
    setCurrentMusic(music);
    setPlaying(true);
    addToPlaylist(music);
    // 清除待播放标记（因为这是直接播放，不是通过"添加到播放序列"）
    pendingPlayMusicRef.current = null;
  };

  // 统一：播放按钮点击（若被新歌限制拦截，则仅提示，不触发播放）
  const handlePlayButtonClickWithGate = (music, e) => {
    const gate = getGateInfo(music);
    if (gate?.blocked) {
      handleGateBlockedClick(e, gate);
      return;
    }
    handlePlayButtonClick(music, e);
  };

  // 仅添加到播放序列（不改变当前播放状态）
  const handleAddToPlaylistClick = (music, e) => {
    e.stopPropagation();
    e.preventDefault(); // 阻止默认行为
    // 立即强制显示播放列表（必须在任何其他操作之前，确保不会被其他逻辑覆盖）
    if (setShowPlaylist) {
      setShowPlaylist(true);
    }
    // 检查是否已在播放列表中，避免重复添加（addToPlaylist 已有去重逻辑，这里只是提前返回）
    const isAlreadyInPlaylist = playlist && playlist.find(m => m.id === music.id);
    if (!isAlreadyInPlaylist) {
      addToPlaylist(music);
      // 添加后再次确保播放列表显示（防止 addToPlaylist 内部有状态更新导致覆盖）
      if (setShowPlaylist) {
        setShowPlaylist(true);
      }
    }
    // 如果没有正在播放其他歌曲，立即播放这首新加入的歌曲
    // 注意：这里需要检查是否正在播放同一首歌曲，避免重复设置
    const isCurrentlyPlayingThis = currentMusic && currentMusic.id === music.id && playing;
    if (!isCurrentlyPlayingThis && (!currentMusic || !playing)) {
      setCurrentMusic(music);
      setPlaying(true);
      // 记录待播放的音乐，用于处理状态更新延迟问题
      pendingPlayMusicRef.current = music;
      // 播放后再次确保播放列表显示
      if (setShowPlaylist) {
        setShowPlaylist(true);
      }
      // 清除待播放标记（延迟清除，给状态更新一些时间）
      setTimeout(() => {
        if (pendingPlayMusicRef.current && pendingPlayMusicRef.current.id === music.id) {
          pendingPlayMusicRef.current = null;
        }
      }, 100);
    }
    // 显示提示信息
    if (e.currentTarget) {
      showUiHint(t('musicList.addToPlaylistSequence'), { anchorEl: e.currentTarget, duration: 1200 });
    }
  };

  const handleOpenDetailClick = (music, e) => {
    e.stopPropagation();
    const currentLang = i18n.language || 'cn';
    spaNavigate(`/${currentLang}/music/${music.id}.html`);
  };

  // 检查音乐是否正在播放
  const isPlaying = (music) => {
    // 检查当前播放状态，以及待播放状态（处理状态更新延迟）
    return (currentMusic && currentMusic.id === music.id && playing) ||
           (pendingPlayMusicRef.current && pendingPlayMusicRef.current.id === music.id);
  };

  const renderListActions = (music, playBlocked) => (
    <div className="space-y-2 sm:space-y-0" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center justify-between gap-2 sm:flex-nowrap sm:whitespace-nowrap">
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:whitespace-nowrap">
            <button
              onClick={(e) => toggleFavorite(music.id, e)}
              className={cx(
                'inline-flex items-center gap-1',
                'h-8 px-2 rounded-lg transition-colors',
                favoritesStatus[music.id] ? 'bg-blue-600 text-white' : 'text-white/70 hover:bg-white/[0.06] border border-white/10'
              )}
              title={favoritesStatus[music.id] ? t('musicList.unfavorite') : t('musicList.favorite')}
            >
              <IconHeart className="w-4 h-4" filled={!!favoritesStatus[music.id]} />
              <span className="text-xs tabular-nums">{formatCount(music.favoritesCount || 0)}</span>
            </button>
          
          <button
            onClick={(e) => handleShare(music, e)}
            className="p-1 text-white/55 rounded hover:bg-white/[0.06] transition-colors flex items-center gap-1"
            title={t('musicList.share')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span className="text-xs text-white/55">{formatCount(music.share_count || 0)}</span>
          </button>
          
          {music.allow_download && (
            <button
              onClick={(e) => handleDownload(music, e)}
              className="p-1 text-white/55 rounded hover:bg-white/[0.06] transition-colors flex items-center gap-1"
              title={t('musicList.download')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="text-xs text-white/55">{formatCount(music.download_count || 0)}</span>
            </button>
          )}
          
          <button
            onMouseEnter={(e) => {
              e.stopPropagation();
              if (hasLyrics(music)) {
                showLyricsPopup(music, e);
              }
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
              hideLyricsPopup();
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (hasLyrics(music)) {
                showLyricsPopup(music, e);
              }
            }}
            disabled={!hasLyrics(music)}
            className={`p-1 rounded transition-colors flex items-center gap-1 ${
              hasLyrics(music)
                ? 'text-white/55 hover:bg-white/[0.06]' 
                : 'text-white/30 cursor-not-allowed opacity-50'
            }`}
            title={hasLyrics(music) ? t('musicList.showLyrics') : t('musicList.noLyrics')}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        {/* 播放次数（最右侧） */}
        <div className="flex flex-wrap items-center gap-1 text-xs text-white/55 sm:flex-nowrap sm:whitespace-nowrap">
          {/* 添加到播放序列 */}
          <button
            type="button"
            onClick={(e) => handleAddToPlaylistClick(music, e)}
            className="p-1 rounded hover:bg-white/[0.06] transition-colors flex items-center justify-center"
            title={t('musicList.addToPlaylistSequence')}
            data-add-to-playlist-btn="true"
          >
            <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
            </svg>
          </button>
          {/* 播放按钮（与封面中间按钮同步） */}
          <button
            type="button"
            onClick={(e) => handlePlayButtonClickWithGate(music, e)}
            className="p-1 rounded hover:bg-white/[0.06] transition-colors flex items-center justify-center"
            title={isPlaying(music) ? t('musicList.pause') : t('musicList.play')}
            aria-disabled={playBlocked ? 'true' : 'false'}
          >
            {isPlaying(music) ? (
              <svg className="w-4 h-4 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            )}
          </button>
          <span>{t('musicList.play')}</span>
          <span className="tabular-nums">{formatCount(music.play_count || 0)}</span>
        </div>
      </div>

      {showAdminActions && (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(music);
              }}
              className={btnOutlineSm}
            >
              {t('musicList.edit')}
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(music.id);
              }}
              className={btnDangerXs}
            >
              {t('musicList.delete')}
            </button>
          )}
        </div>
      )}
    </div>
  );


  if (loading && musics.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-white/70">{t('musicList.loading')}</p>
      </div>
    );
  }

  if (error && musics.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchMusics}
          className={['mt-4', btnOutlineSm].join(' ')}
        >
          {t('musicList.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* “添加到播放列表（可保存）”弹出菜单 */}
      {playlistMenu.open && (
        <div
          className="fixed inset-0 z-[9999]"
          onMouseDown={() => closePlaylistMenu()}
        >
          {/* 遮罩 */}
          <div className="absolute inset-0 bg-black/25 backdrop-blur-[1px]" />

          {/* 弹窗主体：宽度 70%，居中显示 */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            onMouseDown={(e) => e.stopPropagation()}
            ref={playlistMenuRef}
            style={{ width: '70vw', maxWidth: 320, minWidth: 220 }}
          >
            <div className={[theme.surfaces.modalPanel, 'rounded-lg shadow-[0_22px_50px_rgba(0,0,0,0.22)] overflow-hidden'].join(' ')}>
              <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-white/10 bg-white/[0.03]">
                <div className="text-xs font-semibold text-white">{t('musicList.addToPlaylist')}</div>
                <button
                  type="button"
                  className={buttonClass({ variant: 'ghost', size: 'sm', className: 'h-6 w-6 rounded-md hover:bg-white/10' })}
                  onClick={() => closePlaylistMenu()}
                  title={t('playlistManager.close')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M6 18L18 6" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-xs font-semibold border-b border-white/10 bg-blue-500/10 hover:bg-blue-500/15 text-white"
              onClick={(e) => {
                e.stopPropagation();
                openCreatePlaylistDialog(playlistMenu.music);
              }}
            >
              {t('musicList.newPlaylist')}
            </button>
            <div className="max-h-[300px] overflow-auto">
              {playlistMenuLoading ? (
                <div className="px-3 py-2 text-xs text-white/55">加载中...</div>
              ) : (userPlaylists && userPlaylists.length > 0 ? (
                userPlaylists.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full px-3 py-2 text-left text-xs hover:bg-white/[0.04] active:bg-white/[0.06] flex items-center justify-between gap-2 text-white/80"
                    onClick={(e) => addMusicToSavedPlaylist(p, playlistMenu.music, e.currentTarget)}
                  >
                    <span className="truncate">{p.name || `Playlist #${p.id}`}</span>
                    <span className="text-[11px] text-white/45 flex-shrink-0">
                      {Array.isArray(p.music_ids) ? p.music_ids.length : 0}
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-xs text-white/55">
                  暂无播放列表
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      )}

      {/* 新建播放列表对话框（自定义输入） */}
      {createPlaylistDialog.open && (
        <div
          className="fixed inset-0 z-[10000]"
          onMouseDown={() => closeCreatePlaylistDialog()}
        >
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: '78vw', maxWidth: 420, minWidth: 260 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={[theme.surfaces.modalPanel, 'rounded-lg shadow-[0_22px_50px_rgba(0,0,0,0.24)] overflow-hidden'].join(' ')}>
              <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-white/10 bg-white/[0.03]">
                <div className="text-xs font-semibold text-white">{t('musicList.createNewPlaylist')}</div>
                <button
                  type="button"
                  className={buttonClass({ variant: 'ghost', size: 'sm', className: 'h-6 w-6 rounded-md hover:bg-white/10' })}
                  onClick={() => closeCreatePlaylistDialog()}
                  title={t('playlistManager.close')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M6 18L18 6" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-3 space-y-2">
                <label className="block text-xs font-medium text-white/70">
                  播放列表名称
                </label>
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  className={['px-2 py-1 text-xs', theme.inputs.base].join(' ')}
                  placeholder="例如：我喜欢的歌"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const name = String(newPlaylistName || '').trim();
                      if (!name) return;
                      createPlaylistAndAdd(createPlaylistDialog.music, name, e.currentTarget);
                    } else if (e.key === 'Escape') {
                      closeCreatePlaylistDialog();
                    }
                  }}
                />
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    className={btnOutlineSm}
                    onClick={() => closeCreatePlaylistDialog()}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className={buttonClass({ variant: 'primary', size: 'sm' })}
                    disabled={!String(newPlaylistName || '').trim()}
                    onClick={(e) => createPlaylistAndAdd(createPlaylistDialog.music, String(newPlaylistName || '').trim(), e.currentTarget)}
                  >
                    创建
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 批量操作工具栏 */}
      {selectMode && (
        <div className={[theme.surfaces.glassCard, 'rounded-lg p-2 flex items-center justify-between'].join(' ')}>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white text-sm">
              {t('musicList.selectedCount', { count: selectedMusics.size })}
            </span>
            <button
              onClick={toggleSelectAll}
              className={btnOutlineSm}
            >
              {selectedMusics.size === musics.length ? t('musicList.deselectAll') : t('musicList.selectAll')}
            </button>
          </div>
          <div className="flex gap-1">
            <button
              onClick={handleBatchAddToPlaylist}
              className={btnOutlineSm}
            >
              {t('musicList.addToPlaylist')}
            </button>
            <button
              onClick={handleBatchAddToFavorites}
              className={btnDangerXs}
            >
              {t('musicList.addToFavorites')}
            </button>
            <button
              onClick={handleBatchDownload}
              className={btnOutlineSm}
            >
              {t('musicList.download')}
            </button>
            {showAdminActions && (
              <button
                onClick={handleBatchDelete}
                className={btnDangerXs}
              >
                删除
              </button>
            )}
            <button
              onClick={toggleSelectMode}
              className={btnOutlineSm}
            >
              {t('musicList.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* 快捷分类按钮（音乐库页） */}
      {((showQuickCategoryBar === null ? !showAdminActions : !!showQuickCategoryBar)) && (
        <div className="flex flex-wrap items-center gap-1">
          {(() => {
            const items = [
              { key: 'all', label: t('musicList.categoryAll'), value: null },
              { key: 'cn', label: t('musicList.categoryZh'), value: 'cn' },
              { key: 'ja', label: t('musicList.categoryJa'), value: 'ja' },
              { key: 'en', label: t('musicList.categoryEn'), value: 'en' },
              { key: 'world', label: t('musicList.categoryWorld'), value: 'world' },
              { key: 'instrumental', label: t('musicList.categoryInstrumental'), value: 'instrumental' }
            ];

            const isActive = (v) => (v === null ? !quickCategory : quickCategory === v);

            const btnBase =
              'px-2 py-0.5 text-xs rounded-full transition-colors ' +
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/25';
            const btnIdle = theme.buttons.variant.outline;
            const btnActive = 'bg-blue-500/22 border border-blue-400/45 text-white hover:bg-blue-500/26';

            return items.map(it => {
              const active = isActive(it.value);
              return (
                <button
                  key={it.key}
                  type="button"
                  className={`${btnBase} ${active ? btnActive : btnIdle}`}
                  onClick={() => {
                    // 如果切换的分类与当前不同，设置分类切换状态
                    const newCategory = it.value;
                    const currentCategory = quickCategory === undefined ? languageCategory : quickCategory;
                    if (newCategory !== currentCategory && musics.length > 0) {
                      setIsCategoryChanging(true);
                    }
                    setQuickCategory(newCategory);
                    setPage(1);
                  }}
                >
                  {it.label}
                </button>
              );
            });
          })()}
        </div>
      )}

      {/* 搜索、排序和视图切换 */}
      <div className="flex flex-col md:flex-row gap-1.5 items-center justify-between">
        <div className="flex-1 w-full md:w-auto flex gap-1 max-w-[425px] h-9 min-h-[36px] items-center">
          <input
            type="text"
            placeholder={t('musicList.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            onKeyDown={handleSearchKeyDown}
            className={[
              theme.inputs.base,
              'h-7 flex-1 px-1.5 py-0.5 text-xs rounded',
              'bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435]'
            ].join(' ')}
          />
          <button
            onClick={handleSearch}
            className={`h-7 px-1.5 py-0.5 text-xs rounded focus:outline-none focus:ring-1 focus:ring-blue-500 flex items-center gap-0.5 ${
              searchQuery
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            title={searchQuery ? t('musicList.stopSearch') : t('musicList.search')}
          >
            {searchQuery ? (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>{t('musicList.stop')}</span>
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>{t('musicList.search')}</span>
              </>
            )}
          </button>
          {/* 全部播放按钮 */}
          {musics.length > 0 && (
            <button
              onClick={() => {
                if (musics.length > 0) {
                  // 免费/未付费用户：过滤掉“新歌不可播放”的条目，避免点“播放全部”立刻被拦截
                  const playable = musics.filter(m => !getGateInfo(m)?.blocked);
                  if (!playable || playable.length === 0) {
                    // 若全都不可播，提示一次即可
                    const gate0 = getGateInfo(musics[0]);
                    if (gate0?.blocked) {
                      showUiHint(getGateHintText(gate0), { duration: 2200 });
                    }
                    return;
                  }
                  setPlaylist(playable);
                  setCurrentMusic(playable[0]);
                  setPlaying(true);
                  setShowPlaylist(true); // 显示播放列表
                }
              }}
              className={[btnOutlineSm, 'h-7 flex items-center gap-1 whitespace-nowrap flex-shrink-0'].join(' ')}
              title={filterMode === 'favorites' ? t('musicList.playAllFavorites', '全部播放') : t('musicList.playAll')}
            >
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">
                {filterMode === 'favorites' ? t('musicList.playAllFavorites', '全部播放') : t('musicList.playAll')}
              </span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <div className="relative" ref={sortMenuRef}>
            <button
              type="button"
              onClick={() => setSortMenuOpen((open) => !open)}
              className={[
                theme.inputs.base,
                'px-2 py-1 text-xs rounded-lg h-7 leading-tight',
                'text-white/90',
                'bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435]',
                'flex items-center gap-1 min-w-[72px] justify-between'
              ].join(' ')}
              aria-haspopup="listbox"
              aria-expanded={sortMenuOpen}
            >
              <span>
                {sortBy === 'latest'
                  ? t('musicList.sortLatest')
                  : sortBy === 'popular'
                    ? t('musicList.sortPopular')
                    : t('musicList.sortTitle')}
              </span>
              <svg className="w-3 h-3 text-white/70" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
              </svg>
            </button>
            {sortMenuOpen && (
              <div
                className={[
                  'absolute right-0 mt-1 w-24 rounded-lg border border-white/10 z-20 overflow-hidden',
                  'bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435]',
                  'shadow-[0_10px_25px_rgba(0,0,0,0.35)]'
                ].join(' ')}
                role="listbox"
              >
                {[
                  { value: 'latest', label: t('musicList.sortLatest') },
                  { value: 'popular', label: t('musicList.sortPopular') },
                  { value: 'title', label: t('musicList.sortTitle') }
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSortBy(option.value);
                      setPage(1);
                      setSortMenuOpen(false);
                    }}
                    className={[
                      'w-full text-left pl-6 pr-2 py-1 text-xs text-white/90',
                      option.value === sortBy ? 'bg-white/[0.08]' : 'bg-transparent',
                      'hover:bg-white/[0.06] transition-colors'
                    ].join(' ')}
                    role="option"
                    aria-selected={option.value === sortBy}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* 视图切换按钮 */}
          <div className={['flex items-center gap-0 rounded-lg overflow-hidden flex-shrink-0 h-7 border border-white/10 bg-[#111827]/70', 'hover:bg-white/[0.06]'].join(' ')}>
            <button
              type="button"
              onClick={() => {
                setViewMode('card');
                setPage(1);
              }}
              className={`h-7 px-2 text-xs transition-colors flex items-center justify-center flex-shrink-0 ${
                viewMode === 'card'
                  ? 'bg-blue-500/25 text-white'
                  : 'text-white/60 hover:bg-white/[0.05]'
              }`}
              title={t('musicList.viewCard')}
            >
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <div className="w-px h-4 bg-white/15 flex-shrink-0"></div>
            <button
              type="button"
              onClick={() => {
                setViewMode('list');
                setPage(1);
              }}
              className={`h-7 px-2 text-xs transition-colors flex items-center justify-center flex-shrink-0 ${
                viewMode === 'list'
                  ? 'bg-blue-500/25 text-white'
                  : 'text-white/60 hover:bg-white/[0.05]'
              }`}
              title="列表视图"
            >
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* 音乐列表 */}
      <div className="relative">
        {/* 分类切换时的加载指示器（覆盖在列表上方） */}
        {isCategoryChanging && musics.length > 0 && (
          <div className="absolute inset-0 bg-black/35 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-lg">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}
        {musics.length === 0 ? (
          <div className="text-center py-8 text-white/60">
            {t('musicList.noMusic')}
          </div>
        ) : (
          <>
          {viewMode === 'card' ? (
            <>
            {/* 卡片视图 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {musics.map((music) => {
                const gate = getGateInfo(music);
                const playBlocked = !!gate?.blocked;
                const isRecommendedMusic = (() => {
                  try {
                    const raw = music?.tags;
                    const tags = Array.isArray(raw)
                      ? raw
                      : (typeof raw === 'string' ? raw.split(',') : []);
                    const normalized = tags.map(t => String(t || '').trim()).filter(Boolean);
                    // 兼容：历史/手工标记可能使用“精选/推荐歌曲”等
                    return normalized.includes('推荐') || normalized.includes('推荐歌曲') || normalized.includes('精选');
                  } catch (_) {
                    return false;
                  }
                })();
                const isInstrumental = isInstrumentalMusic(music);
                return (
              <div
                key={music.id}
                ref={(el) => {
                  if (el) cardRefs.current[music.id] = el;
                }}
                data-music-id={music.id}
                className={[
                  'rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all group border',
                  theme.surfaces.glassItem,
                  selectMode ? 'cursor-default' : 'cursor-pointer',
                  selectedMusics.has(music.id) ? 'ring-4 ring-blue-500/60' : '',
                  isPlaying(music) ? 'bg-emerald-500/10 border-2 border-emerald-400/35' : 'border-white/10'
                ].filter(Boolean).join(' ')}
                onClick={() => {
                  if (selectMode) {
                    toggleMusicSelection(music.id);
                  } else {
                    // 点击卡片非按钮区域，跳转到详情页
                    const currentLang = i18n.language || 'cn';
                    spaNavigate(`/${currentLang}/music/${music.id}.html`);
                  }
                }}
              >
                {/* 选择复选框 */}
                {selectMode && (
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedMusics.has(music.id)}
                      onChange={() => toggleMusicSelection(music.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </div>
                )}
                {/* 封面图片（保持正方形，手机模式下缩小1/3） */}
                <div className="relative w-2/3 md:w-full aspect-square bg-white/[0.04] border border-white/10 mx-auto md:mx-0">
                  {getCoverImageUrl(music) ? (
                    <img
                      src={getCoverImageUrl(music)}
                      alt={getCurrentLanguageTitle(music)}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/45">
                      <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                      </svg>
                    </div>
                  )}
                  {music.release_date && (
                    <div
                      className="absolute bottom-2 left-2 z-30 px-1.5 py-0.5 rounded-md text-[10px] md:text-[11px] text-white/90 bg-black/45 backdrop-blur-sm border border-white/15 pointer-events-none"
                    >
                      {formatReleaseDate(music.release_date)}
                    </div>
                  )}
                  {/* MTV 图标（如果是视频） */}
                  {hasVideo(music) && (
                    <img
                      src="/icons/mtv.png"
                      alt="MTV"
                      className="absolute top-1 right-1 w-12 h-12 md:w-16 md:h-16 object-contain"
                    />
                  )}
                  {/* 纯音乐图标 */}
                  {!hasVideo(music) && isInstrumental && (
                    <img
                      src="/icons/mcd.png"
                      alt={t('musicList.categoryInstrumental', '纯音乐')}
                      title={t('musicList.categoryInstrumental', '纯音乐')}
                      className="absolute top-1 right-1 w-12 h-12 md:w-16 md:h-16 object-contain"
                    />
                  )}
                  {/* 推荐歌曲徽章（右下角，顶层显示） */}
                  {isRecommendedMusic && (
                    <img
                      src="/icons/goldmedal64.png"
                      alt="Recommended"
                      className="absolute bottom-3 right-1 z-40 w-10 h-10 md:w-12 md:h-12 object-contain pointer-events-none drop-shadow-[0_10px_16px_rgba(0,0,0,0.35)]"
                      loading="lazy"
                      decoding="async"
                      onError={() => {
                        // 仅用于排查：如果资源路径错误，这里会在控制台看到
                        // eslint-disable-next-line no-console
                        console.warn('[MusicList] failed to load /icons/goldmedal64.png');
                      }}
                    />
                  )}
                  {/* 免费/未付费会员：新歌提示条（常驻显示） */}
                  {playBlocked && Number(gate?.daysLeft || 0) > 0 && (
                    <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-none">
                      <div className="w-full px-2 py-1 text-[10px] md:text-[11px] leading-tight text-white backdrop-blur-sm border-t border-white/20 bg-gradient-to-r from-green-500/85 via-emerald-500/85 to-green-600/85">
                        <div className="flex items-center justify-center gap-1">
                          <svg
                            className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0 text-yellow-300 drop-shadow-sm"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm-3.536-7.536a1 1 0 10-1.414 1.414A7.963 7.963 0 0010 14a7.963 7.963 0 004.95-1.95 1 1 0 10-1.414-1.414A5.963 5.963 0 0110 12a5.963 5.963 0 01-3.536-1.536zM7 9a1 1 0 112 0 1 1 0 01-2 0zm4 0a1 1 0 112 0 1 1 0 01-2 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-white drop-shadow-sm">
                            {getGateHintText(gate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* 播放按钮覆盖层 */}
                  {(() => {
                    const isCentered = centeredCardId === music.id;
                    const isCurrentlyPlaying = isPlaying(music);
                    return (
                      <div 
                        // 重要：点击封面应进入详情页（由卡片 onClick 处理），不要触发播放
                        // 因此遮罩层不拦截点击（pointer-events-none），只让播放按钮可点击
                        className={cx(
                          'pointer-events-none absolute inset-0 bg-black bg-opacity-0 transition-all flex items-center justify-center',
                          isCentered ? 'bg-opacity-30' : 'group-hover:bg-opacity-30'
                        )}
                      >
                        <button
                          type="button"
                          className={cx(
                            'pointer-events-auto w-9 h-9 rounded-md flex items-center justify-center transition-opacity',
                            'bg-white/35 backdrop-blur-md border border-white/45 shadow-[0_14px_32px_rgba(0,0,0,0.35)]',
                            'active:scale-[0.98]',
                            playBlocked ? 'opacity-55 cursor-not-allowed grayscale' : '',
                            isCurrentlyPlaying 
                              ? 'opacity-100' 
                              : isCentered 
                                ? 'opacity-100' 
                                : 'opacity-0 group-hover:opacity-100'
                          )}
                          onClick={(e) => {
                            if (playBlocked) return handleGateBlockedClick(e, gate);
                            return handlePlayButtonClick(music, e);
                          }}
                          aria-disabled={playBlocked ? 'true' : 'false'}
                        >
                          {isCurrentlyPlaying ? (
                            <IconPause className="w-5 h-5 text-white drop-shadow-sm" />
                          ) : (
                            <IconPlay className="w-5 h-5 text-white drop-shadow-sm" />
                          )}
                        </button>
                      </div>
                    );
                  })()}
                </div>

                {/* 音乐信息（压缩下半区，整体高度更紧凑） */}
                <div className="p-2">
                  <div className="flex items-center justify-between md:justify-between gap-2">
                    <div className="min-w-0 flex items-center gap-1 flex-1 md:flex-row justify-center md:justify-start">
                      <h3 className="font-semibold text-sm text-white truncate text-center md:text-left md:flex-1 flex items-center gap-1">
                        {hasVideo(music) && (
                          <img
                            src="/icons/mtv-s.png"
                            alt="MTV"
                            className="w-5 h-5 flex-shrink-0 object-contain"
                            title={t('player.hasVideo') || '视频'}
                          />
                        )}
                        {!hasVideo(music) && isInstrumentalMusic(music) && (
                          <img
                            src="/icons/mcd-s.png"
                            alt={t('musicList.categoryInstrumental', '纯音乐')}
                            className="w-5 h-5 flex-shrink-0 object-contain"
                            title={t('musicList.categoryInstrumental', '纯音乐')}
                          />
                        )}
                        {getCurrentLanguageTitle(music)}
                        {cachedStatus[music.id] && (
                          <img 
                            src="/icons/regular_smile.png" 
                            alt={t('musicList.cached')} 
                            className="w-4 h-4 flex-shrink-0 cursor-pointer"
                            title={t('musicList.cachedOffline')}
                            onClick={(e) => {
                              e.stopPropagation();
                              showUiHint(t('musicList.cachedOffline'), { anchorEl: e.currentTarget });
                            }}
                          />
                        )}
                      </h3>
                      <button
                        type="button"
                        onClick={(e) => handleOpenDetailClick(music, e)}
                        className="inline-flex items-center justify-center p-0 h-auto w-auto flex-shrink-0 hover:opacity-80 transition-opacity"
                        title={t('musicList.detail')}
                      >
                        <IconInfo className="w-4 h-4 text-white/70" />
                      </button>
                      <div className="flex items-center gap-2 text-[11px] text-white/55 flex-shrink-0 md:hidden">
                        <span>{formatDuration(music.duration)}</span>
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-[11px] text-white/55 flex-shrink-0">
                      <span>{formatDuration(music.duration)}</span>
                    </div>
                  </div>
                  {/* 压缩：描述/专辑默认不展示（需要可以后续加"展开更多"） */}

                  {/* 标签和专辑名称 */}
                  {((music.tags && music.tags.length > 0) || music.album) && (
                    <div className="flex items-center mt-1 relative" style={{ minHeight: '20px' }}>
                      {/* 左侧标签 */}
                      {music.tags && music.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 flex-shrink-0 z-10">
                          {music.tags
                            .filter(tag => !tag.startsWith('sourceFile='))
                            .slice(0, 2)
                            .map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-1.5 py-0.5 bg-blue-500/15 text-blue-200 text-[10px] rounded border border-blue-400/20"
                            >
                              {translateTag(tag, t)}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* 中间专辑名称 - 绝对居中 */}
                      {music.album && (
                        <span className="text-[12px] text-white/60 truncate text-center absolute left-0 right-0 pointer-events-none">
                          {music.album}
                        </span>
                      )}
                    </div>
                  )}

                  {/* 操作按钮 */}
                  <div className="flex items-center justify-between mt-1 pt-1 border-t border-white/10">
                    {/* 收藏、分享、下载、歌词 */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* 收藏按钮（包含计次文字） */}
                      <button
                        onClick={(e) => toggleFavorite(music.id, e)}
                        className={cx(
                          'h-5 inline-flex items-center gap-1 px-1.5 rounded-md transition-colors text-[10px] whitespace-nowrap flex-shrink-0',
                          favoritesStatus[music.id] ? cardIconBtnActive : cardIconBtnIdle
                        )}
                        title={favoritesStatus[music.id] ? t('musicList.unfavorite') : t('musicList.favorite')}
                      >
                        <IconHeart className="w-4 h-4 flex-shrink-0" filled={!!favoritesStatus[music.id]} />
                        <span className="tabular-nums whitespace-nowrap">{formatCount(music.favoritesCount || 0)}</span>
                      </button>

                      {/* 分享按钮（包含计次文字） */}
                      <button
                        onClick={(e) => handleShare(music, e)}
                        className={cx(
                          'h-5 inline-flex items-center gap-1 px-1.5 rounded-md transition-colors text-[10px] whitespace-nowrap flex-shrink-0',
                          cardIconBtnIdle
                        )}
                        title={t('musicList.share')}
                      >
                        <IconShare className="w-4 h-4 flex-shrink-0" />
                        <span className="tabular-nums whitespace-nowrap">{formatCount(music.share_count || 0)}</span>
                      </button>

                      {/* 下载按钮（包含计次文字） */}
                      {music.allow_download && (
                        <button
                          onClick={(e) => handleDownload(music, e)}
                          className={cx(
                            'h-5 inline-flex items-center gap-1 px-1.5 rounded-md transition-colors text-[10px] whitespace-nowrap flex-shrink-0',
                            cardIconBtnIdle
                          )}
                          title={t('musicList.download')}
                        >
                          <IconDownload className="w-4 h-4 flex-shrink-0" />
                          <span className="tabular-nums whitespace-nowrap">{formatCount(music.download_count || 0)}</span>
                        </button>
                      )}

                      <button
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          if (hasLyrics(music)) {
                            showLyricsPopup(music, e);
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.stopPropagation();
                          hideLyricsPopup();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasLyrics(music)) {
                            showLyricsPopup(music, e);
                          }
                        }}
                        disabled={!hasLyrics(music)}
                        className={cx(
                          cardIconBtnBase,
                          hasLyrics(music) ? cardIconBtnIdle : cardIconBtnDisabled,
                          'flex-shrink-0'
                        )}
                        title={hasLyrics(music) ? t('musicList.showLyrics') : t('musicList.noLyrics')}
                      >
                        <IconLyrics className="w-4 h-4 flex-shrink-0" />
                      </button>
                    </div>
                    
                    {/* 播放次数（最右侧） */}
                    <div className="flex items-center gap-1 text-[10px] text-white/55 flex-shrink-0">
                      {/* 添加到播放序列 */}
                      <button
                        type="button"
                        onClick={(e) => handleAddToPlaylistClick(music, e)}
                        className={cx(
                          'h-5 w-5 inline-flex items-center justify-center rounded-md transition-colors p-0 flex-shrink-0',
                          theme.buttons.variant.outline
                        )}
                        title={t('musicList.addToPlaylistSequence')}
                        data-add-to-playlist-btn="true"
                      >
                        <IconPlus className="w-3.5 h-3.5 flex-shrink-0" />
                      </button>
                      {/* 播放按钮（与封面中间按钮同步，包含文字和播放次数） */}
                      <button
                        type="button"
                        onClick={(e) => handlePlayButtonClickWithGate(music, e)}
                        className={cx(
                          'h-5 inline-flex items-center gap-1 px-1.5 rounded-md transition-colors text-[10px] whitespace-nowrap flex-shrink-0',
                          playBlocked ? 'opacity-55 cursor-not-allowed' : '',
                          isPlaying(music)
                            ? 'bg-emerald-500/15 border border-emerald-400/30 text-white hover:bg-emerald-500/18'
                            : theme.buttons.variant.outline
                        )}
                        title={isPlaying(music) ? t('musicList.pause') : t('musicList.play')}
                        aria-disabled={playBlocked ? 'true' : 'false'}
                      >
                        {isPlaying(music) ? (
                          <IconPause className="w-3.5 h-3.5 flex-shrink-0" />
                        ) : (
                          <IconPlay className="w-3.5 h-3.5 flex-shrink-0" />
                        )}
                        <span className="whitespace-nowrap">{t('musicList.play')}</span>
                        <span className="tabular-nums whitespace-nowrap">{formatCount(music.play_count || 0)}</span>
                      </button>
                      {/* 添加到“保存的播放列表” */}
                      <button
                        type="button"
                        onClick={(e) => openPlaylistMenu(music, e)}
                        className={cx(
                          'h-5 w-5 inline-flex items-center justify-center rounded-md transition-colors p-0 flex-shrink-0',
                          theme.buttons.variant.outline
                        )}
                        title="添加到播放列表"
                        data-open-playlist-menu="true"
                      >
                        <IconPlaylist className="w-3.5 h-3.5 flex-shrink-0" />
                      </button>
                    </div>

                    {/* 管理员操作 */}
                    {showAdminActions && (
                      <div className="flex gap-2">
                        {onEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(music);
                            }}
                            className={btnOutlineSm}
                          >
                            {t('musicList.edit')}
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(music.id);
                            }}
                            className={btnDangerXs}
                          >
                            {t('musicList.delete')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
            </div>
            {/* 卡片视图：底部"加载更多"锚点（不展示分页，滚动自动加载） */}
            <div className="mt-4">
              <div ref={loadMoreSentinelRef} style={{ height: 1 }} />
              <div className="flex justify-center items-center text-xs text-white/55 py-2">
                {loadingMore
                  ? t('musicList.loadMore')
                  : (page < totalPages ? t('musicList.scrollToLoadMore') : t('musicList.loadedAll', { total }))}
              </div>
            </div>
            </>
          ) : (
            /* 列表视图 */
            <div className={theme.tables.wrapper}>
              <table className={cx(theme.tables.table, 'music-list-table')}>
                <thead className={theme.tables.thead}>
                  <tr>
                    {selectMode && (
                      <th className={theme.tables.th}>
                        <input
                          type="checkbox"
                          checked={selectedMusics.size === musics.length && musics.length > 0}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </th>
                    )}
                    <th className={theme.tables.th}>{t('musicList.tableTitle')}</th>
                    <th className={cx(theme.tables.th, 'music-list-only-desktop')}>{t('musicList.tableSubtitle')}</th>
                    <th className={cx(theme.tables.th, 'music-list-only-desktop')}>{t('musicList.tableAlbum')}</th>
                    <th className={theme.tables.th}>{t('musicList.tableDuration')}</th>
                    <th className={cx(theme.tables.th, 'text-center music-list-only-desktop')}>{t('musicList.tableActions')}</th>
                  </tr>
                </thead>
                <tbody className={theme.tables.tbody}>
                  {musics.map((music) => {
                    const gate = getGateInfo(music);
                    const playBlocked = !!gate?.blocked;
                    return (
                    <React.Fragment key={music.id}>
                    <tr
                      className={[
                        theme.tables.tr,
                        selectMode ? 'cursor-default' : 'cursor-pointer',
                        (selectedMusics.has(music.id) || isPlaying(music)) ? 'bg-blue-500/10' : ''
                      ].filter(Boolean).join(' ')}
                      onClick={() => {
                        if (selectMode) {
                          toggleMusicSelection(music.id);
                        } else {
                          // 点击行，跳转到详情页
                          const currentLang = i18n.language || 'cn';
                          spaNavigate(`/${currentLang}/music/${music.id}.html`);
                        }
                      }}
                    >
                      {selectMode && (
                        <td className={theme.tables.td}>
                          <input
                            type="checkbox"
                            checked={selectedMusics.has(music.id)}
                            onChange={() => toggleMusicSelection(music.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        </td>
                      )}
                      <td className={theme.tables.td}>
                        <div className="flex items-center gap-2">
                          {getCoverImageUrl(music) && (
                            <div className="relative">
                              <img
                                src={getCoverImageUrl(music)}
                                alt={getCurrentLanguageTitle(music)}
                                className="w-10 h-10 object-cover rounded"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              {music.release_date && (
                                <div className="absolute bottom-0 left-0 px-1 py-0.5 text-[9px] text-white/90 bg-black/60 rounded-tr pointer-events-none">
                                  {formatReleaseDate(music.release_date)}
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => handlePlayButtonClickWithGate(music, e)}
                            className="p-1 rounded hover:bg-white/[0.06] transition-colors flex items-center justify-center"
                            title={isPlaying(music) ? t('musicList.pause') : t('musicList.play')}
                            aria-disabled={playBlocked ? 'true' : 'false'}
                          >
                            {isPlaying(music) ? (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                              </svg>
                            )}
                          </button>
                          <div className="sm:whitespace-nowrap">
                            <div className="flex items-center gap-2 sm:flex-nowrap sm:whitespace-nowrap">
                              <div className="font-medium text-white text-sm flex items-center gap-1 sm:flex-nowrap sm:whitespace-nowrap">
                                {hasVideo(music) && (
                                  <img
                                    src="/icons/mtv-s.png"
                                    alt="MTV"
                                    className="w-5 h-5 flex-shrink-0 object-contain"
                                    title={t('player.hasVideo') || '视频'}
                                  />
                                )}
                                {!hasVideo(music) && isInstrumentalMusic(music) && (
                                  <img
                                    src="/icons/mcd-s.png"
                                    alt={t('musicList.categoryInstrumental', '纯音乐')}
                                    className="w-5 h-5 flex-shrink-0 object-contain"
                                    title={t('musicList.categoryInstrumental', '纯音乐')}
                                  />
                                )}
                                {getCurrentLanguageTitle(music)}
                                {cachedStatus[music.id] && (
                                  <img 
                                    src="/icons/regular_smile.png" 
                                    alt={t('musicList.cached')} 
                                    className="w-4 h-4 flex-shrink-0 cursor-pointer"
                                    title={t('musicList.cachedOffline')}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      showUiHint(t('musicList.cachedOffline'), { anchorEl: e.currentTarget });
                                    }}
                                  />
                                )}
                              </div>
                              {music.tags && music.tags.filter(tag => !tag.startsWith('sourceFile=')).length > 0 && (
                                <div className="music-list-only-desktop-inline items-center gap-1 text-xs text-white/60">
                                  {music.tags
                                    .filter(tag => !tag.startsWith('sourceFile='))
                                    .slice(0, 2)
                                    .map((tag, idx) => (
                                      <span
                                        key={idx}
                                        className={[theme.badges.base, theme.badges.variant.info, 'text-xs px-1.5 py-0.5 whitespace-nowrap'].join(' ')}
                                      >
                                        {translateTag(tag, t)}
                                      </span>
                                    ))}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={(e) => handleOpenDetailClick(music, e)}
                                className="p-0 h-auto w-auto inline-flex items-center justify-center hover:opacity-80 transition-opacity"
                                title={t('musicList.detail')}
                              >
                                <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <circle cx="12" cy="12" r="9" strokeWidth="2" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 7h.01" />
                                </svg>
                              </button>
                            </div>
                            {music.tags && music.tags.filter(tag => !tag.startsWith('sourceFile=')).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1 music-list-only-mobile">
                                {music.tags
                                  .filter(tag => !tag.startsWith('sourceFile='))
                                  .slice(0, 2)
                                  .map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className={[theme.badges.base, theme.badges.variant.info, 'text-xs px-1.5 py-0.5'].join(' ')}
                                    >
                                      {translateTag(tag, t)}
                                    </span>
                                  ))}
                              </div>
                            )}
                            {(getCurrentLanguageDescription(music) || music.album) && (
                              <div className="music-list-only-mobile mt-1 space-y-0.5 text-xs text-white/60">
                                {getCurrentLanguageDescription(music) && (
                                  <div className="whitespace-normal break-words">
                                    {getCurrentLanguageDescription(music)}
                                  </div>
                                )}
                                {music.album && (
                                  <div className="whitespace-normal break-words text-white/55">
                                    {music.album}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={cx(theme.tables.td, 'music-list-only-desktop')}>
                        {getCurrentLanguageDescription(music) || '--'}
                      </td>
                      <td className={cx(theme.tables.td, 'music-list-only-desktop')}>
                        {music.album || '--'}
                      </td>
                      <td className={cx(theme.tables.td, 'music-list-desktop-nowrap')}>
                        {formatDuration(music.duration)}
                      </td>
                      <td className={cx(theme.tables.td, 'music-list-only-desktop')}>
                        {renderListActions(music, playBlocked)}
                      </td>
                    </tr>
                    <tr className={cx(theme.tables.tr, 'music-list-only-mobile')}>
                      <td className={theme.tables.td} colSpan={listActionColSpan}>
                        {renderListActions(music, playBlocked)}
                      </td>
                    </tr>
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* 列表视图：保留分页按钮 */}
          {viewMode === 'list' && totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className={btnOutlineSm}
              >
                {t('musicList.prevPage')}
              </button>
              <span className="text-white/60">
                {t('musicList.pageInfo', { page, totalPages, total, pageSize })}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={btnOutlineSm}
              >
                {t('musicList.nextPage')}
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* 下载对话框 */}
      <DownloadDialog
        music={selectedMusicForDownload}
        open={downloadDialogOpen}
        onClose={() => {
          setDownloadDialogOpen(false);
          setSelectedMusicForDownload(null);
        }}
        onConfirm={handleDownloadConfirm}
      />

      {/* 歌词弹窗 */}
      {lyricsPopup.show && lyricsPopup.music && lyricsPopup.lyrics && (
        <div
          className="fixed z-[9999] max-w-[420px] overflow-auto"
          style={{
            left: `${lyricsPopup.position.x}px`,
            top: `${lyricsPopup.position.y}px`,
            maxHeight: '300px',
          }}
          onMouseEnter={keepLyricsPopup}
          onMouseLeave={hideLyricsPopup}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 金色玻璃气泡（与播放器浮窗同风格） */}
          <div className="relative rounded-lg bg-gradient-to-b from-amber-200/24 via-amber-500/16 to-gray-900/66 backdrop-blur-md border border-amber-300/35 shadow-[0_16px_38px_rgba(0,0,0,0.56)] shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
            {/* 顶部高光线 */}
            <div className="pointer-events-none absolute inset-x-3 top-1 h-px bg-gradient-to-r from-transparent via-amber-200/75 to-transparent" />
            {/* 金色柔光晕 */}
            <div className="pointer-events-none absolute inset-0 rounded-lg shadow-[0_0_28px_rgba(245,158,11,0.16)]" />

            <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2 border-b border-amber-300/15">
              <h3
                className="font-semibold text-xs truncate"
                style={{
                  color: 'rgb(34, 197, 94)',
                  textShadow: '0 0 10px rgba(34, 197, 94, 0.35)'
                }}
              >
                {getCurrentLanguageTitle(lyricsPopup.music)}
              </h3>
              <button
                onClick={hideLyricsPopup}
                className="h-7 w-7 inline-flex items-center justify-center rounded-lg bg-white/6 hover:bg-white/10 border border-white/12 transition-colors"
                style={{
                  color: 'rgb(34, 197, 94)',
                  textShadow: '0 0 10px rgba(34, 197, 94, 0.25)'
                }}
                title={t('musicList.close')}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M6 18L18 6" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div
              className="px-3 pb-3 text-xs whitespace-pre-wrap leading-relaxed"
              style={{
                color: 'rgb(34, 197, 94)',
                textShadow: '0 0 10px rgba(34, 197, 94, 0.28)'
              }}
            >
              {lyricsPopup.lyrics || t('musicList.lyricsLoading')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicList;

